/**
 * HIRA draft mapper — header fields only (hazards live on the detail page).
 *
 * `suggested_hazards` in the draft is ADVISORY: it is never applied to the
 * form; it is returned on the report as `advisory` for display only.
 * `department` (a name from the backend) resolves to the form's
 * `department_id` against the page's fetched department list.
 */

import { applyDraftWithSpecs, coerceDate, coerceEnum } from './mergeUtils';

export const HIRA_ASSESSMENT_TYPES = ['Activity-based', 'Task-based', 'JSA', 'General'];

const TYPE_ALIASES = {
  'activity based': 'Activity-based',
  activity_based: 'Activity-based',
  'task based': 'Task-based',
  task_based: 'Task-based',
  'job safety analysis': 'JSA',
  jsa: 'JSA',
  'general assessment': 'General',
  general: 'General',
};

function futureDate(value) {
  const d = coerceDate(value);
  if (!d) return null;
  return d > new Date().toISOString().slice(0, 10) ? d : null;
}

function resolveDepartmentId(name, ctx) {
  const departments = ctx.departments || [];
  const hit = departments.find(
    (d) => (d.name || '').toLowerCase() === String(name).trim().toLowerCase()
  );
  return hit ? hit.id : null;
}

const FIELD_SPECS = {
  title: { label: 'Title', coerce: (v) => String(v).slice(0, 500) },
  description: { label: 'Description', coerce: (v) => String(v).slice(0, 5000) },
  assessment_type: {
    label: 'Assessment Type',
    coerce: (v) => coerceEnum(v, HIRA_ASSESSMENT_TYPES, TYPE_ALIASES),
  },
  location: { label: 'Location', coerce: (v) => String(v).slice(0, 500) },
  review_date: { label: 'Review Date', coerce: futureDate },
  department: {
    label: 'Department',
    formKey: 'department_id',
    coerce: resolveDepartmentId,
  },
};

/** applyDraft(draft, currentFormData, ctx = { departments: [{id, name}] }) */
export function applyDraft(draft, currentFormData, ctx = {}) {
  const { suggested_hazards: advisory, ...rest } = draft || {};
  const result = applyDraftWithSpecs(rest, currentFormData, FIELD_SPECS, ctx);
  result.advisory = (advisory || []).slice(0, 10);
  return result;
}

/** "Start from this" mapping for a similar risk-assessment record. */
export function draftFromSimilarRecord(record) {
  if (!record) return {};
  return {
    title: record.title,
    description: record.description,
    assessment_type: record.assessment_type,
    location: record.location,
  };
}
